// Portfolio Administration v1.0 PR 7 — /portfolio-admin/metrics
// 4-row executive dashboard. Replaces the PR 0 ComingSoonPage placeholder.
// Behind PortfolioAdminV1.

import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import {
  useMetricsComposition,
  useMetricsFinancial,
  useMetricsOperationalHealth,
  useMetricsPerformance,
  useRefreshAllMetrics,
} from "@/hooks/portfolioAdmin/usePr7";
import { ComingSoonPage } from "@/components/ComingSoonPage";
import { FinancialRow } from "@/components/portfolioAdmin/metrics/FinancialRow";
import { OperationalHealthRow } from "@/components/portfolioAdmin/metrics/OperationalHealthRow";
import { PerformanceRow } from "@/components/portfolioAdmin/metrics/PerformanceRow";
import { PortfolioCompositionRow } from "@/components/portfolioAdmin/metrics/PortfolioCompositionRow";

export default function MetricsPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <ComingSoonPage
        feature="Portfolio Metrics"
        pr="PR 7"
        description="Executive at-a-glance dashboard. See spec §12."
        backTo="/portfolio-admin/loans"
        backLabel="Back to Loans"
      />
    );
  }
  return <MetricsPageInner />;
}

function MetricsPageInner() {
  const auth = useAuth();
  const canExport =
    auth.hasPermission("Metrics.Export") || auth.hasRoleCode("ADMIN");
  const refresh = useRefreshAllMetrics();

  const composition = useMetricsComposition();
  const performance = useMetricsPerformance();
  const opsHealth = useMetricsOperationalHealth();
  const financial = useMetricsFinancial();

  const isLoading =
    composition.isLoading ||
    performance.isLoading ||
    opsHealth.isLoading ||
    financial.isLoading;

  const hasError =
    composition.isError ||
    performance.isError ||
    opsHealth.isError ||
    financial.isError;

  return (
    <div className="flex flex-col gap-space-4" data-testid="metrics-page">
      <header className="flex items-center justify-between gap-space-2 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-h1 m-0">
            Loan Administration Metrics
          </h1>
          <p className="text-small text-gray-mid m-0">
            Current portfolio snapshot. Auto-cached for 60 seconds.
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => refresh()}
            data-testid="metrics-refresh"
            aria-label="Refresh metrics"
          >
            Refresh
          </Button>
          {canExport && (
            <a
              href="/api/portfolio-admin/metrics/export?format=csv"
              className="inline-flex items-center px-space-3 py-space-1 border border-border-light rounded-sm text-small bg-white-pure hover:bg-cream"
              data-testid="metrics-export-csv"
              aria-label="Export metrics as CSV"
            >
              Export CSV
            </a>
          )}
        </div>
      </header>

      {isLoading && (
        <p className="text-small text-gray-mid" data-testid="metrics-loading">
          Loading dashboard…
        </p>
      )}

      {hasError && (
        <p
          role="alert"
          className="text-small text-status-error"
          data-testid="metrics-error"
        >
          One or more metric rollups failed to load. Try Refresh.
        </p>
      )}

      {composition.data && (
        <PortfolioCompositionRow data={composition.data} />
      )}
      {performance.data && <PerformanceRow data={performance.data} />}
      {opsHealth.data && <OperationalHealthRow data={opsHealth.data} />}
      {financial.data && <FinancialRow data={financial.data} />}
    </div>
  );
}
